import { fabric } from 'fabric';
import { IObjectOptions } from 'fabric/fabric-impl';
import { uniqueId } from 'lodash';
import { Subject } from 'rxjs';
import Vec2 from 'vec2';

import {
    EDGE_STROKE_WIDTH,
    LABEL_OFFSET,
    LABEL_STROKE_WIDTH,
    MEASUREMENT_STROKE_WIDTH,
    OFFSET_BACKSPLASH,
    OFFSET_MEASUREMENT,
    OFFSET_MEASUREMENT_TEXT,
    calcMaxAllowedRadiusForAll,
    calculateEdgeConstraints,
    canApplyRadius,
    cleanSkeletonPoints,
    enforceRadiusConstraints,
    findArrowByEdgeId,
    formatCornerRadiusLabel,
    getArrowsFromOutlines,
    getBumpOutArrowsFromOutlines,
    getDirection,
    getOutlineDiagonalFlagsFromModifiedShape,
    getOutlineEdges,
    getOutlinePointsFromModifiedShape,
    getOutlinePointsFromShape,
    getOutlinePts,
    getRectangleSideItemArrowsFromOutlines,
    getRefinedEdgePoints,
    isPointInsidePolygon,
    rotatePointAboutCenterPoint,
} from '@canvas-common';
import {
    BLACK,
    BUMPOUT_LENGTH,
    CANVAS_OBJECT_TYPE_PREFIX,
    CANVAS_OBJECT_TYPE_SUFFIX,
    CORNER_RADIUS_SIGN,
    DEFAULT_BUMPOUT_DEPTH,
    DEFAULT_BUMPOUT_LENGTH,
    DEFAULT_CORNER_RADIUS,
    DEFAULT_INTERNAL_DIAGONAL_CORNER_LENGTH,
    DEFAULT_LARGEST_SHAPE_DIMENSION,
    DIAGONAL_CORNER_OPTIONS,
    DRAWING_SHAPE_COLOR_PALETTE,
    EDGE_LENGTH_INPUT_LIMIT,
    ELLIPSIS,
    FAUX_SHAPE_INDEX,
    GREY_STROKE_WIDTH,
    GREY_STROKE_WIDTH_BACKSPLASH,
    IMAGE_CUSTOMER_COOKTOP,
    IMAGE_CUSTOMER_DISHWASHER,
    IMAGE_CUSTOMER_RANGE,
    IMAGE_CUSTOMER_REFRIGERATOR,
    IMAGE_CUSTOMER_SINK_APRON,
    IMAGE_CUSTOMER_SINK_DOUBLE_BOWL,
    INCH_QUOTE,
    KEYS,
    LABEL_ELLIPSIS_LIMIT,
    LABEL_LEFT_COLLISION_MARGIN,
    MAGIC_NUMBER,
    MAGIC_NUMBER_2,
    MINIMUM_CUSTOM_SIDE_LENGTH,
    OBJECT_SORT_DELAY,
    OBJECT_SORT_DELAY_LOOPS,
    ROUGH_ESTIMATE_FOR_SHAPE_LABEL_LENGTH,
    SELECTION_CIRCLES,
    SELECTION_CIRCLE_OPACITY,
    SELECTION_CIRCLE_RADIUS,
    SHAPE_LABEL_ALTERED_CORNER_ADJUSTER_DENOMINATOR,
    SHAPE_LABEL_MARGIN,
    SHAPE_NAME_MAX_LENGTH,
    SLAB_SCALE,
    SWATCH_SCALE,
} from '@common/constants';
import { CustomSideType, LineDirection, containsBumpOut, containsRadiusOut } from '@common/enums';
import { ShapeType } from '@common/enums/shape-types.enum';
import {
    MathFunctions,
    convertFromRadiansToDegree,
    getTopLeftVector,
    roundToArrowLengthGranularity,
} from '@common/helpers';
import {
    ApplianceType,
    ArrowInfo,
    BumpOut,
    ColorModel,
    CornerInfo,
    CornerMap,
    CornerRadiusModel,
    DiagonalCornerModel,
    DiagonalCornerType,
    DrawingAppliance,
    DrawingEdge,
    DrawingShape,
    LineSegment,
    PredefinedShape,
    RadiusRangeValues,
    RemovedEdgeAndCorners,
    SideItem,
    Skeleton,
    constrainMinMaxToDefaultMinimum,
    getDefaultRadiusFromRadii,
    getMinMaxFromCorner,
    isCornerRadiusModel,
} from '@common/models';
import { CustomSideModel } from '@common/models/predefined-custom-side.model';
import { getLabelsForArrows } from '@common/utils/indexToLetterLabel';
import { getSideItemsFromShape } from '@common/utils/sideItems.utils';
import ImageUtils from '@core/utils/ImageUtils';

import { getLetterStartFromDrawingEdges } from '../canvas/drawing-tool/builders/measurements-builder';
import { EditableCornerLabel, EditableLengthLabel } from './editableLabel';

import variables from './variables.module.scss';

const drawingTool = {
    buildShapeFromSkeleton: (sData: Skeleton, hasExtraEdging: boolean): DrawingShape | void => {
        const clonedSkeleton: Skeleton = sData.map((bone) => {
            return { pos: bone.pos, depth: bone.depth };
        });

        const cleanSkeleton = cleanSkeletonPoints(clonedSkeleton);
        if (!cleanSkeleton) {
            return;
        }

        const outlineData = getOutlinePts(cleanSkeleton);

        if (!outlineData) {
            return;
        }

        const corners = new CornerMap();

        outlineData.forEach((point) => {
            const id = uniqueId();
            const corner = new CornerInfo(id, point.coords!, point.skIndex!, point.isUpper!);
            corner.isVisibleInCanvas = true;
            corner.hasPredefinedExtraEdging = hasExtraEdging;

            corners[id] = corner;
        });

        const dShapeId = uniqueId();

        const edges = Object.keys(corners).map((key, index) => {
            const startCornerId = corners[key].id;
            let endCornerId;

            if (index === Object.keys(corners).length - 1) {
                endCornerId = Object.values(corners)[0].id;
            } else {
                endCornerId = Object.values(corners)[index + 1].id;
            }

            const dirVec = corners[endCornerId].coords.subtract(corners[startCornerId].coords, true);
            const normalVec = dirVec.rotate(Math.PI / 2, undefined, true).normalize();
            const isVisibleInCanvas = true;
            const sideItem: SideItem = new SideItem(0, [], undefined);
            const bumpOut = new BumpOut();

            const startDisplayVec = new Vec2(0, 0);
            const endDisplayVec = new Vec2(0, 0);

            return {
                id: uniqueId(),
                startCornerId,
                endCornerId,
                startDisplayVec,
                endDisplayVec,
                normalVec,
                dirVec,
                isVisibleInCanvas,
                drawingShapeId: dShapeId,
                sideItem,
                bumpOut,
            } as DrawingEdge;
        });

        // constructor for the shape should really be this whole function, but just using the class for now
        const drawingShape = new DrawingShape({ skeleton: cleanSkeleton, type: ShapeType.PREDEFINED_SHAPE });
        drawingShape.canvasObjectName = CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MAIN + '_' + uniqueId();
        drawingShape.cornerMap = corners;
        drawingShape.drawingEdges = edges;
        drawingShape.removedElementsHistory = { edgesAndCorners: [] };
        drawingShape.id = dShapeId;

        const outlinePoints = outlineData.map((item) => item.coords!);
        drawingShape.highlightPoints = outlinePoints;

        // add initial arrows
        drawingShape.arrowsInfo = getArrowsFromOutlines(
            outlinePoints,
            outlinePoints,
            { diagonals: [], cornerIds: [] },
            []
        ).map((arrow, index) => {
            return {
                ...arrow,
                drawingEdgeId: drawingShape?.drawingEdges[index]?.id,
                isVisibleInCanvas: true,
                isActiveForEdgeCalc: true,
            };
        });

        // find the edge for the letter A
        if (!drawingShape?.establishedLetterStartIndex) {
            // prevents rotating shapes from swapping out edge letters
            drawingShape.establishedLetterStartIndex = getLetterStartFromDrawingEdges(drawingShape);

            const labels = getLabelsForArrows(drawingShape.arrowsInfo, drawingShape.establishedLetterStartIndex);

            // map the new labels into the arrows
            drawingShape?.arrowsInfo?.forEach((arrowInfo: ArrowInfo, index) => {
                arrowInfo.label = labels[index];
            });
        }

        calculateEdgeConstraints(drawingShape);

        return drawingShape;
    },

    rebuildShapeFromNewSkeleton: (sData: Skeleton, oldShape: DrawingShape): void => {
        // may have some similar (ok duplicate) code from buildShapeFromSkeleton:
        // takes an existing shape and applies a new skeleton to it, adjusting all that needs
        // to be adjusted and leaving alone that which does not

        // get new outline data from new skeleton
        const outlineData = getOutlinePts(sData);
        if (!outlineData) {
            return;
        }

        const corners = new CornerMap();

        // update edges as needed based on new outline data, referencing old edges when required
        const edges = Object.keys(oldShape.cornerMap).map((key, index) => {
            corners[key] = oldShape.cornerMap[key];
            corners[key].coords = outlineData[index].coords!;

            const startCornerId = corners[key].id;
            let endCornerIndex;

            if (index === Object.keys(oldShape.cornerMap).length - 1) {
                endCornerIndex = 0;
            } else {
                endCornerIndex = index + 1;
            }
            const endCornerId = Object.values(oldShape.cornerMap)[endCornerIndex].id;
            const dirVec = outlineData[endCornerIndex].coords!.subtract(outlineData[index].coords!, true);

            const normalVec = dirVec.rotate(Math.PI / 2, undefined, true).normalize();
            const isVisibleInCanvas = oldShape.drawingEdges[index].isVisibleInCanvas;

            // *** PREVIOUSLY CLEARED ARROWSINFO ALL THE TIME, BUT THAT BREAKS SIDE ITEMS
            // so if there are any sideItems, don't do this
            // TODO EZE-1448 no idea what needs done, but this feels like a problem waiting to happen
            const oldSideItems = getSideItemsFromShape(oldShape);
            if (!oldSideItems.length) {
                oldShape.drawingEdges[index].sideItem.arrowsInfo = [];
            }
            const startDisplayVec = new Vec2(0, 0);
            const endDisplayVec = new Vec2(0, 0);

            return {
                id: oldShape.drawingEdges[index].id,
                startCornerId,
                endCornerId,
                startDisplayVec,
                endDisplayVec,
                normalVec,
                dirVec,
                isVisibleInCanvas,
                drawingShapeId: oldShape.id,
                sideItem: oldShape.drawingEdges[index].sideItem,
                bumpOut: oldShape.drawingEdges[index].bumpOut,
                customSideType: oldShape.drawingEdges[index].customSideType,
                realRadius: oldShape.drawingEdges[index].realRadius,
                radiusSagitta: oldShape.drawingEdges[index].radiusSagitta,
            } as DrawingEdge;
        });

        // adjust the directional and normal vectors inside the removedElementsHistory list
        if (oldShape.removedElementsHistory) {
            oldShape.removedElementsHistory.edgesAndCorners.forEach((remEdgeCorner: RemovedEdgeAndCorners) => {
                remEdgeCorner.drawingEdge.dirVec = remEdgeCorner.appliedCorners[1].coords!.subtract(
                    remEdgeCorner.appliedCorners[0].coords!,
                    true
                );
                remEdgeCorner.drawingEdge.normalVec = remEdgeCorner.drawingEdge.dirVec
                    .normalize(true)
                    .rotate(Math.PI / 2, undefined, true)
                    .normalize();
            });
        }

        oldShape.skeleton = sData;
        oldShape.cornerMap = corners;
        oldShape.drawingEdges = edges;

        const outlinePoints = outlineData.map((item) => item.coords!);

        oldShape.highlightPoints = outlinePoints;
        const arrows = getArrowsFromOutlines(outlinePoints, outlinePoints, { diagonals: [], cornerIds: [] }, []);
        arrows.forEach((arrow, index) => {
            if (oldShape.arrowsInfo) {
                oldShape.arrowsInfo[index] = {
                    ...oldShape.arrowsInfo[index],
                    path: arrow.path,
                    size: arrow.size,
                };
            }
        });
    },

    buildFauxShape: (): DrawingShape => {
        const fauxShape = new DrawingShape({ skeleton: [], type: ShapeType.FAUX_SHAPE });
        fauxShape.arrowsInfo = [];
        fauxShape.id = ShapeType.FAUX_SHAPE;
        return fauxShape;
    },

    cloneShapes: (shapes: DrawingShape[]): DrawingShape[] => {
        return shapes.map((shape: DrawingShape) => {
            return DrawingShape.cloneDrawingShape(shape, false, false);
        });
    },

    buildShapeFromPredefinedShape: (pShape: PredefinedShape): Array<DrawingShape> => {
        const dShapes: Array<DrawingShape> = [];
        pShape.skeletonData.forEach((sData) => {
            const dShape = drawingTool.buildShapeFromSkeleton(sData, pShape?.hasInteriorDiagonal);

            if (dShape) {
                dShapes.push(dShape);
            }
        });
        return dShapes;
    },

    addDrawingShape: (
        canvas: fabric.Canvas,
        shapes: DrawingShape[],
        onEdit: (editedArrow: ArrowInfo, drawingShape: DrawingShape) => void,
        useOverlay: boolean,
        activeAppliance: string,
        activeDrawingShape: DrawingShape
    ): void => {
        const opt: IObjectOptions = {
            fill: 'transparent',
            selectable: false,
            stroke: variables.borderColor,
            strokeWidth: GREY_STROKE_WIDTH,
            strokeUniform: true,
            //Movement:
            lockMovementX: false,
            lockMovementY: false,
            //Scaling:
            lockScalingX: true,
            lockScalingY: true,
            //Rotation:
            lockRotation: true,
            //frame/bounidngBox:
            hasControls: false,
            hasBorders: false,
        };

        shapes
            .filter((shape: DrawingShape) => shape.type !== ShapeType.FAUX_SHAPE)
            .forEach((shape) => {
                if (!shape?.cornerMap) {
                    return;
                }

                shape.hideAllLabels();
                shape.editableLabels = [];

                drawingTool.edgeAndCornerCalcs(shape);
                // check to ensure all radius constraints are respected and redo calcs if needed
                if (drawingTool.someRadiiNeededConstraining(shape)) {
                    drawingTool.edgeAndCornerCalcs(shape);
                }

                opt.name = shape?.canvasObjectName;
                const outlinePoints = getOutlinePointsFromModifiedShape(shape);
                const outline = new fabric.Polygon(outlinePoints, opt);

                shape.fabricGroup.fill = 'transparent';
                shape.fabricGroup.stroke = 'transparent';
                shape.fabricGroup.selectable = true;
                shape.fabricGroup.lockMovementX = false;
                shape.fabricGroup.lockMovementY = false;
                shape.fabricGroup.lockScalingX = true;
                shape.fabricGroup.lockScalingY = true;
                shape.fabricGroup.lockRotation = true;
                shape.fabricGroup.hasControls = false;
                shape.fabricGroup.hasBorders = false;
                shape.fabricGroup.perPixelTargetFind = true;

                shape.fabricGroup.remove(...shape.fabricGroup.getObjects());
                shape.fabricGroup.addWithUpdate(outline);
                shape.fabricGroup.name = shape.canvasObjectName + CANVAS_OBJECT_TYPE_SUFFIX.SHAPE_GROUP;

                drawingTool.addDrawingEdgeToCanvas(canvas, shape, onEdit, useOverlay, activeDrawingShape);
                drawingTool.addAppliancesToCanvas(canvas, shape, activeAppliance);

                canvas.add(shape.fabricGroup);
            });

        shapes
            .filter((shape: DrawingShape) => shape.type === ShapeType.FAUX_SHAPE)
            .forEach((shape) => {
                drawingTool.addAppliancesToCanvas(canvas, shape, activeAppliance);
            });

        drawingTool.tryObjectSorting(canvas, shapes, OBJECT_SORT_DELAY_LOOPS);
    },

    tryObjectSorting: (canvas: fabric.Canvas, shapes: DrawingShape[], countDown: number) => {
        // delay allowing the drawing of appliances to catch up so they can be considered during object ordering
        setTimeout(() => {
            if (countDown === 0 || drawingTool.allShapesAndAppliancesAreDrawn(canvas, shapes)) {
                drawingTool.bringAllLabelsForward(canvas);
            } else if (countDown > 0) {
                drawingTool.tryObjectSorting(canvas, shapes, countDown - 1);
            }
        }, OBJECT_SORT_DELAY);
    },

    /**
     * attempts to determine if all the shapes and appliances in the drawingShapes array
     * have been added to the canvas. This is used to verify that rendering passes and useEffects
     * have all completed their tasks.
     * @param canvas the canvas where the shapes should be drawn
     * @param drawingShapes the drawingShapesArray to test
     */
    allShapesAndAppliancesAreDrawn: (canvas: fabric.Canvas, drawingShapes: DrawingShape[]): boolean => {
        // count all shapes and appliances that Should be drawn
        let shapesAndAppliancesExpected = 0;
        drawingShapes.forEach((shape: DrawingShape) => {
            // count all shapes we would Expect to be drawn, excluding the faux shape
            if (shape.id !== ShapeType.FAUX_SHAPE) {
                shapesAndAppliancesExpected++;
            }
            // count all appliances, including those attached to the faux shape
            shape.appliances.forEach(() => {
                shapesAndAppliancesExpected++;
            });
        });

        // count all shapes and appliances that actually Are drawn
        const drawnItemSet: Set<string> = new Set(); // create a Set because appliances are listed 3 times by name in the group

        //Removing duplicates is necessary for saved estimates see EZE-1158
        const canvasObjectsWithoutDuplicates = Array.from(
            canvas
                .getObjects()
                .reduce((map, obj) => map.set(obj.name, obj), new Map())
                .values()
        );

        canvasObjectsWithoutDuplicates.forEach((o: fabric.Object) => {
            // count top level objects that are shapes or appliances (roaming or currently selected appliances)
            if (
                o.name?.includes(CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MAIN) ||
                o.name?.includes(CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE)
            ) {
                drawnItemSet.add(o.name);
            }
            // dig down into the fabric groups and count any attached appliances
            if (o.name?.includes(CANVAS_OBJECT_TYPE_SUFFIX.SHAPE_GROUP)) {
                const objectsInGroup = (o as fabric.Group)
                    .getObjects()
                    .filter((item) => item.name?.includes(CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE));
                objectsInGroup.forEach((item: any) => {
                    drawnItemSet.add(item.name);
                });
            }
        });

        const shapesAndAppliancesDrawn = drawnItemSet.size;

        return shapesAndAppliancesExpected === shapesAndAppliancesDrawn;
    },

    // eslint-disable-next-line sonarjs/cognitive-complexity
    edgeAndCornerCalcs: (shape: DrawingShape): void => {
        if (!shape) {
            return;
        }

        // first loop through sets the edge start and end display points
        let edgeIndex = 0;
        shape.drawingEdges.map((edge) => {
            const startPt = shape?.cornerMap[edge.startCornerId];
            const endPt = shape?.cornerMap[edge.endCornerId];

            if (!startPt || !endPt) {
                console.warn('null startPt or endPt', edge, shape, shape.cornerMap);
                return;
            }
            // some preliminary math for the radii and edge calculations (angle relative to x axis = orientation
            const dirVec = edge.dirVec;
            const angleBetweenVectAndX = Math.atan2(dirVec.y, dirVec.x);
            const orientationAngle = Math.round(convertFromRadiansToDegree(angleBetweenVectAndX));

            let nextEdgeIndex;
            if (edgeIndex === shape?.drawingEdges.length - 1) {
                nextEdgeIndex = 0;
            } else {
                nextEdgeIndex = edgeIndex + 1;
            }

            // angle between the two adjacent edges
            const angleBetweenNextVectAndX = Math.atan2(
                shape.drawingEdges[nextEdgeIndex].dirVec.y,
                shape.drawingEdges[nextEdgeIndex].dirVec.x
            );
            const cornerAngleInRadians = Math.PI - (angleBetweenVectAndX - angleBetweenNextVectAndX);
            let cornerAngleInDegrees = Math.round(convertFromRadiansToDegree(cornerAngleInRadians));
            if (cornerAngleInDegrees > 180) {
                cornerAngleInDegrees -= 360;
            } // makes all interior angles negative
            endPt.angle = cornerAngleInDegrees;
            endPt.orientationAngle = Math.round(orientationAngle);

            const cos = Math.cos(orientationAngle * (Math.PI / 180));
            const sin = Math.sin(orientationAngle * (Math.PI / 180));

            // some tomfoolery to get the batwing angles working / TODO generalize for all angles later
            const batAdjustEnd = endPt.angle! % 135 || endPt?.diagonalLength ? 1 : Math.cos(Math.PI / 4) / MAGIC_NUMBER;
            const batAdjustStart =
                startPt.angle! % 135 || startPt?.diagonalLength ? 1 : Math.cos(Math.PI / 4) / MAGIC_NUMBER;

            edgeIndex += 1;

            // adjust the edge lengths (actual on the canvas only) if we need to account for corner radii
            let spX = startPt.coords.x;
            let spY = startPt.coords.y;
            let epX = endPt.coords.x;
            let epY = endPt.coords.y;

            if (endPt.isVisibleInCanvas) {
                if (endPt.diagonalLength) {
                    // adjust side for a diagonal
                    const endDiagSideLen =
                        endPt.angle! % 135
                            ? Math.sqrt(Math.pow(endPt.diagonalLength, 2) / 2)
                            : endPt.diagonalLength * (MAGIC_NUMBER_2 / 12);
                    epX -= endDiagSideLen * cos * batAdjustEnd;
                    epY -= endDiagSideLen * sin * batAdjustEnd;
                } else if (endPt.size && endPt.size > 0.75) {
                    // adjust side for a corner radius
                    epX -= endPt.size * cos * batAdjustEnd;
                    epY -= endPt.size * sin * batAdjustEnd;
                }
            }
            if (startPt.isVisibleInCanvas) {
                if (startPt.diagonalLength) {
                    // adjust side for a diagonal
                    const startDiagSideLen =
                        startPt.angle! % 135
                            ? Math.sqrt(Math.pow(startPt.diagonalLength, 2) / 2)
                            : startPt.diagonalLength * (MAGIC_NUMBER_2 / 12);
                    spX += startDiagSideLen * cos * batAdjustStart; // fix
                    spY += startDiagSideLen * sin * batAdjustStart; // fix
                } else if (startPt.size && startPt.size > 0.75) {
                    // adjust side for a corner radius
                    spX += startPt.size * cos * batAdjustStart;
                    spY += startPt.size * sin * batAdjustStart;
                }
            }
            // add the edge display points adjusted for corner radii or diagonals if needed
            // keep a copy in the corner for clip calcs
            edge.startDisplayVec = new Vec2(spX, spY);
            startPt.replacementEndCoords = edge.startDisplayVec;
            edge.endDisplayVec = new Vec2(epX, epY);
            endPt.replacementStartCoords = edge.endDisplayVec;
        });

        // second loop through sets the radii center points (based on the start and end points of the edges from the first loop)
        edgeIndex = 0;
        shape.drawingEdges.map((edge) => {
            const endPt = shape.cornerMap[edge.endCornerId];
            let nextEdgeIndex;
            if (edgeIndex === shape.drawingEdges.length - 1) {
                nextEdgeIndex = 0;
            } else {
                nextEdgeIndex = edgeIndex + 1;
            }
            edgeIndex += 1;

            //find radius center point by using the respective start and end display points on the adjacent edges
            if (endPt.size && endPt.size > 0.75 && endPt.isVisibleInCanvas) {
                const intersectionPt = MathFunctions.getInterceptPoint(
                    edge.endDisplayVec,
                    edge.normalVec,
                    shape.drawingEdges[nextEdgeIndex].startDisplayVec,
                    shape.drawingEdges[nextEdgeIndex].normalVec,
                    true
                );
                endPt.radiusCenterPoint! = intersectionPt!;
            }
        });

        calcMaxAllowedRadiusForAll(shape);
    },

    someRadiiNeededConstraining: (shape: DrawingShape): boolean => {
        return Object.values(shape?.cornerMap).some((corner) => {
            if (!corner.size) {
                corner.size = DEFAULT_CORNER_RADIUS;
            }
            if (!corner.isVisibleInCanvas) {
                return false;
            }
            if (corner.diagonalLength) {
                return false;
            }

            const origCornerSize = corner.size;
            const radius = enforceRadiusConstraints(corner, corner.size);
            if (radius === origCornerSize) {
                return false;
            } else {
                corner.size = radius;
                return true;
            }
        });
    },

    isApplianceOnCountertop: (
        newCenterPoint: Vec2,
        appliance: DrawingAppliance,
        shape: DrawingShape,
        strict: boolean
    ): boolean => {
        if (strict) {
            const applianceCorners = [
                new Vec2(newCenterPoint.x - appliance.width / 2, newCenterPoint.y - appliance.height / 2),
                new Vec2(newCenterPoint.x + appliance.width / 2, newCenterPoint.y - appliance.height / 2),
                new Vec2(newCenterPoint.x + appliance.width / 2, newCenterPoint.y + appliance.height / 2),
                new Vec2(newCenterPoint.x - appliance.width / 2, newCenterPoint.y + appliance.height / 2),
            ];

            const rotatedApplianceCorners = applianceCorners.map((corner) => {
                return rotatePointAboutCenterPoint(corner, newCenterPoint, 0 - appliance.angle);
            });

            // check to ensure NO appliance corners IS OUTSIDE the countertop polygon
            let allCornersIn = !rotatedApplianceCorners.some((rotCorner) => {
                return !isPointInsidePolygon(getOutlinePointsFromModifiedShape(shape), rotCorner);
            });

            // also check to ensure NO countertop corner IS INSIDE the appliance outline
            allCornersIn =
                allCornersIn &&
                !getOutlinePointsFromModifiedShape(shape).some((counterCorner) => {
                    return isPointInsidePolygon(rotatedApplianceCorners, counterCorner);
                });

            return allCornersIn;
        }

        return isPointInsidePolygon(getOutlinePointsFromModifiedShape(shape), newCenterPoint);
    },

    // eslint-disable-next-line sonarjs/cognitive-complexity
    addAppliancesToCanvas: (canvas: fabric.Canvas, shape: DrawingShape, activeAppliance: string): void => {
        if (!shape) {
            return;
        }
        shape.appliances.map(async (appliance) => {
            let applianceImageSource;
            // TODO look at refactoring this bit
            switch (appliance.applianceModel.applianceType) {
                case ApplianceType.SINK:
                    applianceImageSource = await ImageUtils.getBase64FromURL(
                        ImageUtils.imgUrl(appliance.applianceModel.beautyShotImageUrl, 300)
                    );
                    break;
                case ApplianceType.SINK_LABOR:
                    applianceImageSource = appliance.applianceModel.name.includes('Apron')
                        ? IMAGE_CUSTOMER_SINK_APRON
                        : IMAGE_CUSTOMER_SINK_DOUBLE_BOWL;
                    break;
                case ApplianceType.DISHWASHER:
                    applianceImageSource = IMAGE_CUSTOMER_DISHWASHER;
                    break;
                case ApplianceType.REFRIGERATOR:
                    applianceImageSource = IMAGE_CUSTOMER_REFRIGERATOR;
                    break;
                case ApplianceType.COOKTOP:
                    applianceImageSource = IMAGE_CUSTOMER_COOKTOP;
                    break;
                case ApplianceType.RANGE:
                    applianceImageSource = IMAGE_CUSTOMER_RANGE;
                    break;
                default:
                    break;
            }

            if (applianceImageSource) {
                const applianceName = `${CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE}_${appliance.id}`;
                fabric.Image.fromURL(applianceImageSource, (applianceImage: fabric.Image) => {
                    let scale = 1 / 10; // default scale for appliances with no sizing
                    // otherwise, use the ratio of the appliance's height to its image height for the scale
                    if (appliance.height && applianceImage.height) {
                        scale = appliance.height / applianceImage.height;
                    }
                    applianceImage.set({
                        left: appliance.centerPoint.x,
                        top: appliance.centerPoint.y,
                        angle: appliance.angle,
                        centeredRotation: true,
                        originX: 'middle',
                        originY: 'middle',
                        selectable: true,
                        lockRotation: true,
                        lockScalingX: true,
                        lockScalingY: true,
                        hasControls: false,
                        name: applianceName,
                        scaleX: scale,
                        scaleY: scale,
                    });

                    if (activeAppliance === applianceName || shape.type === ShapeType.FAUX_SHAPE) {
                        canvas.add(applianceImage);
                        if (activeAppliance === applianceName) {
                            canvas.setActiveObject(applianceImage);
                        }
                    } else {
                        shape.fabricGroup.addWithUpdate(applianceImage);
                    }
                });
            }
        });
    },

    addDrawingEdgeToCanvas: (
        canvas: fabric.Canvas,
        shape: DrawingShape,
        onEdit: (editedArrow: ArrowInfo, drawingShape: DrawingShape) => void,
        useOverlay: boolean,
        activeDrawingShape: DrawingShape
        // eslint-disable-next-line sonarjs/cognitive-complexity
    ): void => {
        if (!shape) {
            return;
        }

        const optEdge: IObjectOptions = {
            fill: 'transparent',
            selectable: false,
            stroke: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
            strokeLineCap: 'round',
            strokeWidth: EDGE_STROKE_WIDTH,
        };

        optEdge.data = {
            parentName: shape.canvasObjectName,
            highlightable: true,
        };

        shape.drawingEdges
            .filter((edge) => edge.isVisibleInCanvas) // this removes anything with custom side options as well
            .map((edge) => {
                const endPt = shape.cornerMap[edge.endCornerId];
                // add diagonal corner for predefined shapes with internal diagonal corner
                if (endPt.hasPredefinedExtraEdging && !endPt.diagonalLength && endPt.angle && endPt.angle < 0) {
                    endPt.diagonalLength = DEFAULT_INTERNAL_DIAGONAL_CORNER_LENGTH;
                    endPt.label = DIAGONAL_CORNER_OPTIONS.INSIDE_CORNER;

                    // EZE-341 on minified app, the .set functions are crashing the app
                    if (endPt.labelText !== undefined && 'set' in endPt.labelText) {
                        endPt.labelText.set('text', endPt.label);
                    }
                    if (endPt.labelMeasure !== undefined && 'set' in endPt.labelMeasure) {
                        endPt.labelMeasure.set('text', `${endPt.diagonalLength}${INCH_QUOTE}`);
                    }
                }

                if (
                    endPt.diagonalLength &&
                    endPt.replacementStartCoords &&
                    endPt.replacementEndCoords &&
                    (endPt.replacementStartCoords.x !== endPt.replacementEndCoords.x ||
                        endPt.replacementStartCoords.y !== endPt.replacementEndCoords.y)
                ) {
                    // add the diagonal if needed
                    optEdge.name = `${CANVAS_OBJECT_TYPE_PREFIX.DIAGONAL}_${edge.id}`;

                    const diagonalPoints = getRefinedEdgePoints(
                        [
                            endPt.replacementStartCoords.x,
                            endPt.replacementStartCoords.y,
                            endPt.replacementEndCoords.x,
                            endPt.replacementEndCoords.y,
                        ],
                        optEdge.strokeWidth
                    );
                    const diagObj = new fabric.Line(diagonalPoints, optEdge);
                    shape.fabricGroup.addWithUpdate(diagObj);
                } else if (
                    endPt.angle &&
                    endPt.size &&
                    endPt.size > 0.75 &&
                    endPt.isVisibleInCanvas &&
                    endPt.radiusCenterPoint
                ) {
                    // add a rounded corner radius arc if appropriate
                    const angleOffset = endPt.angle > 0 ? 0 : 90; // not needed for exterior radii
                    const angleOffset2 = endPt.angle === 135 ? 45 : 0;
                    const fillColor = endPt.angle > 0 ? '' : '#fff'; // not needed for exterior radii

                    // build the actual corner radius arc and add it to the canvas. Reuses edge.id
                    const circle = new fabric.Circle({
                        name: `${CANVAS_OBJECT_TYPE_PREFIX.CORNER_RADIUS_ARC}_${edge.id}`,
                        radius: endPt.size,
                        left: endPt.radiusCenterPoint.x,
                        top: endPt.radiusCenterPoint.y,
                        originX: 'center',
                        originY: 'center',
                        angle: endPt.orientationAngle,
                        startAngle: 0 - angleOffset + angleOffset2,
                        endAngle: endPt.angle % 90 ? 45 - angleOffset + angleOffset2 : 90 - angleOffset,
                        stroke: variables.secondaryColor,
                        strokeWidth: EDGE_STROKE_WIDTH,
                        fill: fillColor,
                        selectable: false,
                    });
                    shape.fabricGroup.addWithUpdate(circle);
                }

                // add the edges, shortened for corner radii or diagonals if needed
                if (edge.customSideType === undefined) {
                    // add straight edge
                    optEdge.name = `${CANVAS_OBJECT_TYPE_PREFIX.SHAPE_EDGE}_${edge.id}`;
                    const drawingPoints = getRefinedEdgePoints(
                        [edge.startDisplayVec.x, edge.startDisplayVec.y, edge.endDisplayVec.x, edge.endDisplayVec.y],
                        optEdge.strokeWidth
                    );

                    const edgeObj = new fabric.Line(drawingPoints, optEdge);

                    shape.fabricGroup.addWithUpdate(edgeObj);
                } else if (containsRadiusOut(edge)) {
                    shape.fabricGroup.addWithUpdate(drawingTool.addRadiusOut(edge, false, useOverlay));
                } else if (containsBumpOut(edge)) {
                    shape.fabricGroup.addWithUpdate(drawingTool.addBumpOut(edge, false, useOverlay));
                    drawingTool.addBumpOutArrows(canvas, shape, edge, onEdit, activeDrawingShape);
                }
            });

        calculateEdgeConstraints(shape);
    },

    addRadiusOut: (edge: DrawingEdge, forClipping: boolean, useOverlay: boolean): fabric.Path => {
        // use the potentially modified edge endpoints to find the radius center point for the radius out
        const chordLength = MathFunctions.getDistanceBetween(edge.startDisplayVec, edge.endDisplayVec);
        const radius = MathFunctions.calculateRadiusFromArc(chordLength, edge.radiusSagitta!);
        const halfChord = chordLength / 2;
        if (!forClipping) {
            edge.realRadius =
                (edge.radiusSagitta! * edge.radiusSagitta! + halfChord * halfChord) / (2 * edge.radiusSagitta!);
        }

        const bigArc = edge.radiusSagitta! > halfChord ? '1' : '0';

        const pathString =
            'M ' +
            edge.startDisplayVec.x.toString() +
            ' ' +
            edge.startDisplayVec.y.toString() +
            ' A ' +
            radius.toString() +
            ' ' +
            radius.toString() +
            ' 0 ' +
            bigArc +
            ' 0 ' +
            edge.endDisplayVec.x.toString() +
            ' ' +
            edge.endDisplayVec.y.toString() +
            (forClipping ? ' Z' : '');

        const pathOptions = forClipping
            ? {}
            : {
                  stroke: variables.secondaryColor,
                  strokeWidth: EDGE_STROKE_WIDTH,
                  fill: useOverlay ? variables.secondaryColor : 'transparent',
                  opacity: useOverlay ? SELECTION_CIRCLE_OPACITY : 1,
                  name: `${CANVAS_OBJECT_TYPE_PREFIX.RADIUS_OUT}_${edge.id}`,
              };
        return new fabric.Path(pathString, pathOptions);
    },

    // eslint-disable-next-line sonarjs/cognitive-complexity
    addBumpOut: (edge: DrawingEdge, forClipping: boolean, useOverlay: boolean): fabric.Path => {
        const edgeLength = MathFunctions.getDistanceBetween(edge.startDisplayVec, edge.endDisplayVec);
        let bumpOutLength = edge.bumpOut?.length ? edge.bumpOut?.length : DEFAULT_BUMPOUT_LENGTH;
        const bumpOutDepth = edge.bumpOut?.depth ? edge.bumpOut?.depth : DEFAULT_BUMPOUT_DEPTH;

        // if the bumpOut length is longer than the edge length, create a bumpOut that is half the edge length
        if (edgeLength < bumpOutLength! + 2) {
            bumpOutLength = roundToArrowLengthGranularity(edgeLength / 2);
            edge.bumpOut!.clockwiseRemainderLength = roundToArrowLengthGranularity((edgeLength - bumpOutLength) / 2);
            edge.bumpOut!.counterClockwiseRemainderLength = roundToArrowLengthGranularity(
                (edgeLength - bumpOutLength) / 2
            );
        } else if (
            // if the edge length is different from the sum of the component lengths, distribute the + or - mismatch to the remaining edges
            edgeLength !==
            bumpOutLength! + edge.bumpOut!.clockwiseRemainderLength + edge.bumpOut!.counterClockwiseRemainderLength
        ) {
            const misMatch =
                bumpOutLength! +
                edge.bumpOut!.clockwiseRemainderLength +
                edge.bumpOut!.counterClockwiseRemainderLength -
                edgeLength;
            edge.bumpOut!.clockwiseRemainderLength -= misMatch / 2;
            edge.bumpOut!.counterClockwiseRemainderLength -= misMatch / 2;
        }

        // build array of points for bump out
        const bumpVec: Vec2[] = [];
        if (edge.bumpOut?.clockwiseRemainderLength) {
            bumpVec.push(
                edge.startDisplayVec.add(
                    edge.dirVec.normalize(true).multiply(edge.bumpOut?.clockwiseRemainderLength, true),
                    true
                )
            );
        } else {
            const midPoint = MathFunctions.getMidpoint(edge.startDisplayVec, edge.endDisplayVec);
            bumpVec.push(midPoint.subtract(edge.dirVec.normalize(true).multiply(bumpOutLength / 2, true), true));
        }
        bumpVec.push(bumpVec[0].add(edge.normalVec.multiply(bumpOutDepth!, true), true));
        bumpVec.push(bumpVec[1].add(edge.dirVec.normalize(true).multiply(bumpOutLength, true), true));
        bumpVec.push(bumpVec[2].subtract(edge.normalVec.multiply(bumpOutDepth!, true), true));

        const firstPathPortion = !forClipping
            ? edge.startDisplayVec.x.toString() + ' ' + edge.startDisplayVec.y.toString() + ' L '
            : '';

        const lastPathPortion = !forClipping
            ? ' L ' + edge.endDisplayVec.x.toString() + ' ' + edge.endDisplayVec.y.toString()
            : ' Z';

        let pathString = 'M ' + firstPathPortion;
        for (let b = 0; b < 4; b++) {
            pathString = pathString + bumpVec[b].x.toString() + ' ' + bumpVec[b].y.toString() + (b < 3 ? ' L ' : '');
        }
        pathString = pathString + lastPathPortion;

        const pathOptions = forClipping
            ? {}
            : {
                  stroke: variables.secondaryColor,
                  strokeWidth: EDGE_STROKE_WIDTH,
                  fill: useOverlay ? variables.secondaryColor : 'transparent',
                  opacity: useOverlay ? SELECTION_CIRCLE_OPACITY : 1,
                  name: `${CANVAS_OBJECT_TYPE_PREFIX.BUMP_OUT}_${edge.id}`,
              };
        const path = new fabric.Path(pathString, pathOptions);

        if (!forClipping && edge.bumpOut?.depth) {
            edge.bumpOut = Object.assign(new BumpOut(), {
                length: bumpOutLength,
                counterClockwiseRemainderLength: !edge.bumpOut.counterClockwiseRemainderLength
                    ? roundToArrowLengthGranularity((edgeLength - bumpOutLength) / 2)
                    : edge.bumpOut.counterClockwiseRemainderLength,
                clockwiseRemainderLength: !edge.bumpOut.clockwiseRemainderLength
                    ? roundToArrowLengthGranularity((edgeLength - bumpOutLength) / 2)
                    : edge.bumpOut.clockwiseRemainderLength,
                depth: edge.bumpOut?.depth,
                arrowsInfo: [],
            });
        }
        return path;
    },

    getLargestShapeDimension: (
        shapeWidthsAndHeights: number[],
        defaultLargestShapeDimension = DEFAULT_LARGEST_SHAPE_DIMENSION
    ): number => {
        return shapeWidthsAndHeights.length ? Math.max(...shapeWidthsAndHeights) : defaultLargestShapeDimension;
    },

    calcSlabScale: (drawingShapes: DrawingShape[]): number => {
        let slabScale = SLAB_SCALE.DEFAULT;
        if (drawingShapes.length < 2) {
            return slabScale;
        }

        // find largest dimension for any drawing shape
        const shapeWidthsAndHeights: number[] = [];
        drawingShapes.forEach((shape) => {
            if (shape.type !== ShapeType.FAUX_SHAPE) {
                const shapeBoundingRect = shape.fabricGroup?.getBoundingRect(true, true);
                // as slab images tend to be wider then tall, adjust the width in consideration of the scale
                shapeWidthsAndHeights.push(shapeBoundingRect.width * SLAB_SCALE.WIDTH_ADJUST, shapeBoundingRect.height);
            }
        });
        const largestShapeDimension = drawingTool.getLargestShapeDimension(shapeWidthsAndHeights);

        // set slab scale based on largest shape dimension
        if (largestShapeDimension > 300) {
            slabScale = SLAB_SCALE.MIN;
        } else {
            // polynomial formula (based on heuristics) to dynamically determine reasonable slab scale
            slabScale =
                SLAB_SCALE.POLYN_A * largestShapeDimension * largestShapeDimension +
                SLAB_SCALE.POLYN_B * largestShapeDimension +
                SLAB_SCALE.POLYN_C;
        }
        slabScale = Math.max(Math.min(slabScale, SLAB_SCALE.MAX), SLAB_SCALE.MIN);

        return slabScale;
    },

    fillColor: (
        canvas: fabric.Canvas,
        color: ColorModel,
        drawingShapes: DrawingShape[],
        useOverlay: boolean,
        imageElement: HTMLImageElement
        // eslint-disable-next-line sonarjs/cognitive-complexity
    ): void => {
        // fill with slab image if available
        let patternTrans = [1, 0, 0, 1, 0, 0];
        const scale = color?.slabImageUrl ? drawingTool.calcSlabScale(drawingShapes) : SWATCH_SCALE;
        patternTrans = [1 / scale, 0, 0, 1 / scale, 0, 0];
        for (let i = 0; i < canvas.getObjects().length; i++) {
            // fill color for main shape polygon and backsplash polygon contained in the backsplash group
            if (canvas.getObjects()[i].name?.includes(CANVAS_OBJECT_TYPE_SUFFIX.SHAPE_GROUP)) {
                const objectsInGroup = (canvas.getObjects()[i] as fabric.Group).getObjects();
                objectsInGroup.forEach((item: any) => {
                    if (item.name?.includes(CANVAS_OBJECT_TYPE_PREFIX.BACKSPLASH_GROUP)) {
                        const children = item.getObjects();
                        children?.map((child: fabric.Object) => {
                            if (child.name?.includes(CANVAS_OBJECT_TYPE_PREFIX.POLYGON_BACKSPLASH)) {
                                child.set(
                                    'fill',
                                    new fabric.Pattern({
                                        source: imageElement,
                                        repeat: 'repeat',
                                        // transform matrix to change the pattern, imported from svgs.
                                        patternTransform: patternTrans,
                                    })
                                );
                            }
                        });
                    } else if (item.name?.includes(CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MAIN)) {
                        drawingShapes.forEach((shape: DrawingShape) => {
                            if (item.name === shape?.canvasObjectName) {
                                item.clipPath = drawingTool.getClipGroup(shape, false);
                                if (useOverlay) {
                                    const overlayShape = drawingTool.getClipGroup(shape, true);
                                    shape.fabricGroup.addWithUpdate(overlayShape);
                                }
                            }
                        });
                        item.set(
                            'fill',
                            new fabric.Pattern({
                                source: imageElement,
                                repeat: 'repeat',
                                // transform matrix to change the pattern, imported from svgs.
                                patternTransform: patternTrans,
                            })
                        );
                    }
                });
            }
        }

        //This render is needed as it is inside async module
        //but only needs to execute once outside the loop
        canvas.renderAll();
    },

    getOutlinePointsFromCorners: (corners: CornerInfo[], index: number) => {
        const findCorner = (skIndex: number, isUpper: boolean) =>
            corners.find((item) => item.skIndex === skIndex && item.isUpper === isUpper);

        const prevUpperOutlinePt = findCorner(index - 1, true);
        const nextUpperOutlinePt = findCorner(index, true);
        const prevBottomOutlinePt = findCorner(index - 1, false);
        const nextBottomOutlinePt = findCorner(index, false);

        return { prevUpperOutlinePt, nextUpperOutlinePt, prevBottomOutlinePt, nextBottomOutlinePt };
    },

    // eslint-disable-next-line sonarjs/cognitive-complexity
    getClipGroup: (dShape: DrawingShape, useOverlay: boolean): fabric.Group => {
        const sk = dShape.skeleton;
        const clipShapeList = [];

        for (let i = 1; i < sk.length; i++) {
            const { prevUpperOutlinePt, nextUpperOutlinePt, prevBottomOutlinePt, nextBottomOutlinePt } =
                drawingTool.getOutlinePointsFromCorners([...Object.values(dShape.cornerMap)], i);

            if (!(prevUpperOutlinePt && nextUpperOutlinePt && prevBottomOutlinePt && nextBottomOutlinePt)) {
                continue;
            }

            const boneOutline = [prevUpperOutlinePt, nextUpperOutlinePt, nextBottomOutlinePt, prevBottomOutlinePt];
            const clipBoneOutline = [];

            // next three lines do nothing but allow clipBoneOutline to work within the loop and outside
            // any ideas on how to remove this, great
            const noPoint = new Vec2(0, 0);
            clipBoneOutline.push(noPoint);
            clipBoneOutline.pop();

            boneOutline.forEach((outlinePt) => {
                const r = outlinePt.size;
                if (((r && r > 0.75) || outlinePt.diagonalLength) && outlinePt.isVisibleInCanvas) {
                    clipBoneOutline.push(outlinePt.replacementStartCoords);
                    clipBoneOutline.push(outlinePt.replacementEndCoords);
                    if (
                        outlinePt.angle &&
                        outlinePt.angle > 0 &&
                        outlinePt.orientationAngle !== undefined &&
                        outlinePt.radiusCenterPoint &&
                        !outlinePt.diagonalLength
                    ) {
                        // also add an internal circle to the clipping area for external corners
                        const angleOffset2 = outlinePt.angle === 135 ? 45 : 0;
                        const clipCircle = new fabric.Circle({
                            radius: r,
                            left: outlinePt.radiusCenterPoint.x,
                            top: outlinePt.radiusCenterPoint.y,
                            originX: 'center',
                            originY: 'center',
                            startAngle: outlinePt.orientationAngle + angleOffset2,
                            endAngle: outlinePt.orientationAngle + 90, // ...why this doesn't include angleOffset2
                        });
                        if (useOverlay) {
                            clipCircle.set({ fill: variables.secondaryColor, opacity: SELECTION_CIRCLE_OPACITY });
                        }
                        clipShapeList.push(clipCircle); // comment this line to see only clip polygons
                    }
                } else {
                    // just a normal corner, nothing to see here
                    clipBoneOutline.push(outlinePt.coords);
                }
            });

            let clipPoly;
            if (useOverlay) {
                clipPoly = new fabric.Polygon(clipBoneOutline, {
                    selectable: false,
                    fill: variables.secondaryColor,
                    opacity: SELECTION_CIRCLE_OPACITY,
                });
            } else {
                clipPoly = new fabric.Polygon(clipBoneOutline, {
                    selectable: false,
                });
            }
            clipShapeList.push(clipPoly);
            //clipShapeList.pop(); // uncommenting this shows just the circe clips
        }

        // overlays for radius and bump outs are handled during the non-clipping drawing functions
        if (!useOverlay) {
            // add any needed radius outs
            dShape.drawingEdges
                .filter((edge) => edge.isVisibleInCanvas && containsRadiusOut(edge))
                .map((edge) => {
                    clipShapeList.push(drawingTool.addRadiusOut(edge, true, false));
                });

            // add any needed bump outs
            dShape.drawingEdges
                .filter((edge) => edge.isVisibleInCanvas && containsBumpOut(edge))
                .map((edge) => {
                    clipShapeList.push(drawingTool.addBumpOut(edge, true, false));
                });
        }

        return new fabric.Group(clipShapeList, {
            absolutePositioned: true,
        });
    },

    clearCanvas: (canvas: fabric.Canvas): void => {
        if (!canvas) {
            return;
        }
        canvas.remove(...canvas.getObjects());
    },

    addArrowInfoData: (shape: DrawingShape): void => {
        const outlinePointsForArrows = getOutlinePointsFromModifiedShape(shape, true);
        const outlineDiagonalFlags = getOutlineDiagonalFlagsFromModifiedShape(shape);
        const outlineEdges = getOutlineEdges(shape);
        shape.highlightPoints = outlinePointsForArrows;
        const outlinePointsForInteriorArrows = getOutlinePointsFromShape(shape);
        const previousShapeValues = { ...shape };
        shape.arrowsInfo = getArrowsFromOutlines(
            outlinePointsForArrows,
            outlinePointsForInteriorArrows,
            outlineDiagonalFlags,
            outlineEdges
        );
        // TODO EZE-1500 This is horked, it's really doing things backwards, but I'm afraid to fix it
        previousShapeValues?.arrowsInfo?.forEach((oldArrow, index) => {
            const newArrowInfo = shape.arrowsInfo;
            if (shape.arrowsInfo && newArrowInfo && newArrowInfo[index]) {
                const currentArrowInfo: ArrowInfo = {
                    ...newArrowInfo[index],
                    label: oldArrow.label, // this is critical!!!
                    drawingEdgeId: !oldArrow.isInterior
                        ? oldArrow.drawingEdgeId
                        : shape.arrowsInfo[index].drawingEdgeId,
                    isVisibleInCanvas: oldArrow?.isVisibleInCanvas ?? true,
                    isActiveForEdgeCalc: oldArrow?.isActiveForEdgeCalc ?? true,
                    minimumLength: oldArrow.minimumLength,
                    maximumLength: oldArrow.maximumLength,
                };

                if (shape.arrowsInfo && !currentArrowInfo.isDiagonalCorner) {
                    shape.arrowsInfo[index] = currentArrowInfo;
                }
            }
        });
    },

    createArrowGroup: (arrowInfo: ArrowInfo, opt: IObjectOptions): fabric.Group | undefined => {
        if (!arrowInfo.isVisibleInCanvas) {
            return;
        }

        const arrowPts = arrowInfo.path;
        const mainSegment = new fabric.Polyline([arrowPts[0], arrowPts[1]], opt);
        const leftArrowWing = new fabric.Polyline([arrowPts[2], arrowPts[3], arrowPts[4]], opt);
        const rightArrowWing = new fabric.Polyline([arrowPts[5], arrowPts[6], arrowPts[7]], opt);

        return new fabric.Group([mainSegment, leftArrowWing, rightArrowWing], { selectable: false });
    },

    addMeasurements: (
        canvas: fabric.Canvas,
        shapes: DrawingShape[],
        color: string,
        onEdit: (editedArrow: ArrowInfo, drawingShape: DrawingShape) => void,
        activeDrawingShape: DrawingShape
        // eslint-disable-next-line sonarjs/cognitive-complexity
    ): void => {
        if (shapes?.length === 0) {
            return;
        }
        const opt: IObjectOptions = {
            stroke: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
            strokeWidth: MEASUREMENT_STROKE_WIDTH,
            fill: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
            selectable: false,
            data: {},
        };

        const interiorOpt = { ...opt, stroke: color, fill: color };

        shapes
            .filter((shape: DrawingShape) => shape.type !== ShapeType.FAUX_SHAPE)
            .map((shape: DrawingShape) => {
                drawingTool.addArrowInfoData(shape);
                shape?.arrowsInfo?.map((arrowInfo: ArrowInfo) => {
                    const arrowPts = arrowInfo.path;
                    const group = drawingTool.createArrowGroup(arrowInfo, arrowInfo.isInterior ? interiorOpt : opt);
                    if (group) {
                        group.data = {
                            parentName: shape?.canvasObjectName,
                            highlightable: true,
                            inInterior: arrowInfo.isInterior,
                        };
                        if (arrowInfo.isInterior) {
                            group.name = `${CANVAS_OBJECT_TYPE_PREFIX.INTERIOR_ARROW}`;
                            canvas.add(group);
                        } else {
                            shape.fabricGroup.addWithUpdate(group);
                        }
                    }

                    drawingTool.addMeasureText(
                        shape,
                        canvas,
                        {
                            startPoint: arrowPts[0],
                            endPoint: arrowPts[1],
                        },
                        arrowInfo.label ?? '',
                        arrowInfo,
                        color,
                        onEdit,
                        activeDrawingShape
                    );
                });
            });
    },

    getMeasurementPosition: (arrow: LineSegment, text: fabric.Text, normalVec: Vec2): Vec2 => {
        const pos = arrow.startPoint.add(arrow.endPoint, true).multiply(0.5);
        const boundRc = text.getBoundingRect();
        const direction = getDirection(arrow);

        let sign = 1;
        switch (direction) {
            case LineDirection.TOP:
                pos.add(normalVec.multiply(OFFSET_MEASUREMENT_TEXT + boundRc.height, true));
                pos.set(pos.x - boundRc.width * 0.5, pos.y, false);
                break;
            case LineDirection.BOTTOM:
                pos.add(normalVec.multiply(OFFSET_MEASUREMENT_TEXT, true));
                pos.set(pos.x - boundRc.width * 0.5, pos.y, false);
                break;
            case LineDirection.VERTICAL_LEFT:
                pos.set(pos.x - boundRc.width - OFFSET_MEASUREMENT, pos.y - boundRc.height * 0.5, false);
                break;
            case LineDirection.VERTICAL_RIGHT:
                pos.set(pos.x + OFFSET_MEASUREMENT + LABEL_OFFSET, pos.y - boundRc.height * 0.5, false);
                break;
            case LineDirection.LEFT:
                sign = normalVec.y < 0 ? -1 : 0;
                pos.set(pos.x - boundRc.width, pos.y + sign * boundRc.height, false);
                break;
            default:
                sign = normalVec.y < 0 ? -1 : 0;
                pos.set(pos.x, pos.y + sign * boundRc.height, false);
                break;
        }

        //Pos refinement
        if (sign === 0 && (direction === LineDirection.LEFT || direction === LineDirection.RIGHT)) {
            pos.set(pos.x, pos.y + OFFSET_MEASUREMENT, false);
        }
        return pos;
    },
    // Adds edge label and editable length attribute. On edit callback is called when the user is finished editing the length.
    addMeasureText: (
        shape: DrawingShape,
        canvas: fabric.Canvas,
        arrow: LineSegment,
        label: string,
        arrowInfo: ArrowInfo,
        color: string,
        onEdit: (editedArrow: ArrowInfo, drawingShape: DrawingShape) => void,
        activeDrawingShape: DrawingShape
    ): void => {
        arrowInfo.label = label;
        if (!arrowInfo.isVisibleInCanvas) {
            return;
        }
        shape.editableLabels.push(
            new EditableLengthLabel(shape, arrow, arrowInfo, label, onEdit, canvas, color, activeDrawingShape)
        );
    },

    // eslint-disable-next-line sonarjs/cognitive-complexity
    positionShapeLabel: (shape: DrawingShape): Vec2 => {
        let shapeTextPos: Vec2 | undefined;
        let longestHorizontalTopEdge = { nextBestFlatTop: new Vec2(0, 0), length: 0 };

        const requiredEdgeLength = ROUGH_ESTIMATE_FOR_SHAPE_LABEL_LENGTH;

        // horizontal top edges only
        const horizontalTopEdges = shape.drawingEdges.filter(
            (edge: DrawingEdge) => !Math.round(edge.dirVec.y) && edge.dirVec.x < 0
        );

        // sort edges by highest point per edge
        horizontalTopEdges.sort(
            (a, b) =>
                Math.max(a.startDisplayVec.y, a.endDisplayVec.y) - Math.max(b.startDisplayVec.y, b.endDisplayVec.y)
        );

        // this loop attempts set the position of the shape label on the highest horizontal edge that is long enough
        horizontalTopEdges.forEach((edge: DrawingEdge) => {
            // edge lengths appropriate for the shape label may be impacted by corner radii or diagonals (display end) or not (real)
            let realOrDisplayEnd;
            if (shape.cornerMap[edge.endCornerId].size) {
                const leftShiftForRadiusCorner = Math.pow(
                    shape.cornerMap[edge.endCornerId].size!,
                    1 / SHAPE_LABEL_ALTERED_CORNER_ADJUSTER_DENOMINATOR
                );
                //only apply adjustment if no diagonalLength and corner is visible
                const shapeNotVisibleInCanvas = !shape.cornerMap[edge.endCornerId].isVisibleInCanvas;
                const shapeHasDiagonalLength = !!shape.cornerMap[edge.endCornerId].diagonalLength;

                realOrDisplayEnd = new Vec2(
                    edge.endDisplayVec.x -
                        (shapeNotVisibleInCanvas || shapeHasDiagonalLength ? 0 : leftShiftForRadiusCorner),
                    edge.endDisplayVec.y
                );
            } else {
                realOrDisplayEnd = shape.cornerMap[edge.endCornerId].coords;
            }
            const edgeDisplayLength = MathFunctions.getDistanceBetween(edge.startDisplayVec, realOrDisplayEnd);
            if (edgeDisplayLength > requiredEdgeLength && shapeTextPos === undefined) {
                shapeTextPos = realOrDisplayEnd;
            } else if (edgeDisplayLength > longestHorizontalTopEdge.length) {
                longestHorizontalTopEdge = { nextBestFlatTop: realOrDisplayEnd, length: edgeDisplayLength };
            }
        });

        // if we still haven't found a place for the shape label (because there are no horizontal tops or none long enough)
        if (!shapeTextPos) {
            if (longestHorizontalTopEdge.length) {
                // there is at least one horizontal top and we will use the longest one
                shapeTextPos = longestHorizontalTopEdge.nextBestFlatTop;
            } else {
                // find the top most point and it's radius center point, then adjust x from there
                const outlinePoints = getOutlinePointsFromShape(shape);
                const topLeft = getTopLeftVector(outlinePoints);
                const topmostCorner = Object.values(shape.cornerMap).find((item) => item.coords === topLeft);

                // Find edge that starts with the topmost corner
                const topmostEdge = shape.drawingEdges.find((edge) => edge.startCornerId === topmostCorner?.id);

                // Find corner adjacent to the left of the topmost corner
                // i.e. the corner that ends the edge started by the topmost corner
                const leftyTopmostCorner = Object.values(shape.cornerMap).find(
                    (corner) => corner.id === topmostEdge?.endCornerId
                );

                if (leftyTopmostCorner && leftyTopmostCorner.size) {
                    const EDGE_LENGTH_FACTOR = 5; // arbitrary number that looks good in design area
                    shapeTextPos = new Vec2(
                        leftyTopmostCorner.coords.x + requiredEdgeLength / EDGE_LENGTH_FACTOR,
                        leftyTopmostCorner.coords.y - requiredEdgeLength / EDGE_LENGTH_FACTOR
                    );
                } else {
                    shapeTextPos = MathFunctions.addVectors(topLeft, new Vec2(-15, 13));
                }
            }
        }

        return shapeTextPos;
    },

    nextAvailableShapeNumber: (shapes: DrawingShape[]): number => {
        return (
            Array.from({ length: shapes.length + 1 }, (_, index) => index + 1).find(
                (shapeNum) => !shapes.some((shape) => shape.label === `Shape ${shapeNum}`)
            ) || 0
        );
    },

    addShapeText: (canvas: fabric.Canvas, shapes: DrawingShape[], color: string): void => {
        let shapeCounter = 0;

        shapes
            .filter((shape) => shape.type !== ShapeType.FAUX_SHAPE)
            .forEach((shape) => {
                const shapeLabel: string = shape?.label || `Shape ${drawingTool.nextAvailableShapeNumber(shapes)}`;
                shapeCounter++;

                const shapeTextPos = drawingTool.positionShapeLabel(shape);

                const shapeNameText = new fabric.IText(shapeLabel, {
                    fontFamily: variables.fontFamily,
                    stroke: color,
                    fill: color,
                    height: 100,
                    strokeWidth: LABEL_STROKE_WIDTH,
                    left: shapeTextPos?.x + SHAPE_LABEL_MARGIN,
                    top: shapeTextPos?.y + SHAPE_LABEL_MARGIN,
                    editable: true,
                    fontSize: 5,
                    hoverCursor: 'pointer',
                    hasControls: false,
                    lockMovementX: true,
                    lockMovementY: true,
                    name: `${CANVAS_OBJECT_TYPE_PREFIX.SHAPE_LABEL}_${shapeLabel}`,
                    data: {
                        parentName: shape?.canvasObjectName,
                        highlightable: true,
                        inInterior: true,
                    },
                });

                shapeNameText.on('editing:entered', () => {
                    shapeNameText?.hiddenTextarea?.setAttribute('maxlength', SHAPE_NAME_MAX_LENGTH.toString());
                });

                shapeNameText.onKeyDown = (e: KeyboardEvent) => {
                    if (e.key === KEYS.ENTER || e.key === KEYS.TAB) {
                        shapeNameText.exitEditing();
                    }
                };

                if (shape) {
                    shape.label = shapeLabel;
                    shape.counterLabel = shapeCounter;
                    shape.onTextChange = new Subject();
                    shapeNameText.on('editing:exited', () => {
                        if (shapeNameText.text?.trim() !== '') {
                            shape?.onTextChange?.next(shapeNameText.text || '');
                        } else {
                            shapeNameText.set('text', shape.label);
                            shape?.onTextChange?.next(shapeNameText.text);
                        }
                    });
                }

                canvas.add(shapeNameText);

                drawingTool.replaceITextWithEllipsisIfNeeded(shapeNameText, getOutlinePointsFromShape(shape));
            });
    },
    replaceITextWithEllipsisIfNeeded: (label: fabric.Text, currShapePolygon: Vec2[]): void => {
        if (label.aCoords === undefined) {
            return;
        }
        const shapeLabelPolygon = [];
        shapeLabelPolygon.push(new Vec2(label.aCoords.tl.x + LABEL_LEFT_COLLISION_MARGIN, label.aCoords.tl.y));
        shapeLabelPolygon.push(
            new Vec2(label.aCoords.tl.x + label.width! + LABEL_LEFT_COLLISION_MARGIN, label.aCoords.tl.y)
        );
        shapeLabelPolygon.push(
            new Vec2(
                label.aCoords.tl.x + label.width! + LABEL_LEFT_COLLISION_MARGIN,
                label.aCoords.tl.y + label.height!
            )
        );
        shapeLabelPolygon.push(
            new Vec2(label.aCoords.tl.x + LABEL_LEFT_COLLISION_MARGIN, label.aCoords.tl.y + label.height!)
        );

        const result = MathFunctions.polygonCollisions(shapeLabelPolygon, [currShapePolygon]);
        if (result[0]) {
            const labelLength = label.text?.length;
            if (labelLength! > LABEL_ELLIPSIS_LIMIT) {
                label.set('text', label.text?.slice(0, label.text?.length - LABEL_ELLIPSIS_LIMIT) + ELLIPSIS);
                drawingTool.replaceITextWithEllipsisIfNeeded(label, currShapePolygon);
            }
        }
    },

    rotateAndPositionSideItemLabel: (label: fabric.Text, directionVector: Vec2): void => {
        const dirVec = directionVector;

        const angleBetweenVectAndX = Math.atan2(dirVec.y, dirVec.x);
        let convertedAngle = convertFromRadiansToDegree(angleBetweenVectAndX);
        if (convertedAngle <= -90) {
            convertedAngle += 180;
        } else if (convertedAngle > 90) {
            convertedAngle -= 180;
        }

        // adjust the label position based on the required rotation (EE-966)
        const adjY = 1.7;
        const adjX = 8;
        const cos = Math.cos((convertedAngle * Math.PI) / 180);
        const sin = Math.sin((convertedAngle * Math.PI) / 180);
        label.top! -= adjY * cos + adjX * sin;
        label.left! -= adjX * cos - adjY * sin;

        // now actually do the rotation
        label.centeredRotation = false; // it's way off without this line
        label.rotate(convertedAngle);
    },

    addRectangleSideItemArrowInfoMeasurement: (
        _shape: DrawingShape,
        arrow: LineSegment,
        arrowInfo: ArrowInfo
    ): fabric.IText => {
        const diffVec = arrow.endPoint.subtract(arrow.startPoint, true);
        const normalVec = diffVec.rotate(Math.PI / 2, undefined, true).normalize();
        arrowInfo.size = roundToArrowLengthGranularity(diffVec.length());
        const text = new fabric.IText('', {
            stroke: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
            fill: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
            strokeWidth: MEASUREMENT_STROKE_WIDTH,
            fontSize: 2.5,
            fontFamily: variables.fontFamily,
            selectable: false,
            name: CANVAS_OBJECT_TYPE_PREFIX.BACKSPLASH_MEASURE_LABEL + uniqueId(),
        });

        // get position of the measurements
        const pos = drawingTool.getMeasurementPosition(arrow, text, normalVec);

        text.left = pos.x;
        text.top = pos.y;

        return text;
    },

    addRectangleSideItemArrows: (
        canvas: fabric.Canvas,
        shape: DrawingShape,
        outlinePoints: Vec2[],
        drawingEdge: DrawingEdge,
        onEdit: (editedArrow: ArrowInfo, shape: DrawingShape, enteredValue?: number) => void,
        activeDrawingShape: DrawingShape
    ): fabric.Group | undefined => {
        if (!shape) {
            return;
        }
        let arrowsgroup: fabric.Group | undefined = new fabric.Group([], { selectable: false });
        const arrowOpt: IObjectOptions = {
            stroke: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
            strokeWidth: MEASUREMENT_STROKE_WIDTH,
            fill: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
            selectable: false,
            data: {},
        };

        const removedArrowInfoByDrawingEdge = shape?.arrowsInfo?.find(
            (arrowInfo) => arrowInfo.drawingEdgeId === drawingEdge.id && !arrowInfo.isVisibleInCanvas
        );

        // create and return side item arrow group
        const arrows = getRectangleSideItemArrowsFromOutlines(outlinePoints, removedArrowInfoByDrawingEdge?.label);

        // remove previous arrows from the sire item arrowInfo array
        if (drawingEdge?.sideItem?.arrowsInfo?.length > 0) {
            drawingEdge.sideItem.arrowsInfo = [];
        }
        arrows.map((arrow) => {
            drawingEdge?.sideItem?.arrowsInfo?.push({
                path: arrow.path,
                label: arrow.label,
                isVisibleInCanvas: true,
            });
        });

        drawingEdge?.sideItem?.arrowsInfo?.map((arrowInfo, index) => {
            drawingTool.removeObjectByName(canvas, `${CANVAS_OBJECT_TYPE_PREFIX.BACKSPLASH_ARROW}_${index}`, true);

            arrowsgroup = drawingTool.createArrowGroup(arrowInfo, arrowOpt);
            if (!arrowsgroup) {
                return;
            }

            // add labels for each arrow
            const arrowPts = arrowInfo.path;
            const groupCenterPoint = shape.fabricGroup.getCenterPoint();
            const groupCenterVec2 = new Vec2(groupCenterPoint.x, groupCenterPoint.y);
            const arrowPtsAbsolute = [arrowPts[0].add(groupCenterVec2, true), arrowPts[1].add(groupCenterVec2, true)];
            const lineSegmentAbsolute = {
                startPoint: arrowPtsAbsolute[0],
                endPoint: arrowPtsAbsolute[1],
            };
            const lineSegment = {
                startPoint: arrowPts[0],
                endPoint: arrowPts[1],
            };
            arrowsgroup.data = {
                parentName: shape.canvasObjectName,
                highlightable: true,
                name: `${CANVAS_OBJECT_TYPE_PREFIX.BACKSPLASH_ARROW}_${index}`,
            };
            if (arrowInfo.label?.length !== 0) {
                // Shape lengths can only be written by changing the old side length
                const oldArrowInfo = findArrowByEdgeId(drawingEdge.id, shape);
                if (!oldArrowInfo || !oldArrowInfo.label) {
                    return;
                }
                shape.editableLabels.push(
                    new EditableLengthLabel(
                        shape,
                        lineSegmentAbsolute,
                        oldArrowInfo,
                        oldArrowInfo.label,
                        onEdit,
                        canvas,
                        BLACK,
                        activeDrawingShape
                    )
                );
                shape.fabricGroup.add(arrowsgroup);

                const diffVec = lineSegment.endPoint.subtract(lineSegment.startPoint, true);
                arrowInfo.size = roundToArrowLengthGranularity(diffVec.length());
            } else {
                arrowInfo.isSideItemPresent = true;
                arrowInfo.drawingEdgeId = drawingEdge.id;

                const measurementText = drawingTool.addRectangleSideItemArrowInfoMeasurement(
                    shape,
                    lineSegment,
                    arrowInfo
                );

                shape.editableLabels.push(
                    new EditableLengthLabel(
                        shape,
                        lineSegmentAbsolute,
                        arrowInfo,
                        arrowInfo.label,
                        onEdit,
                        canvas,
                        BLACK,
                        activeDrawingShape
                    )
                );

                shape.fabricGroup.add(arrowsgroup!, measurementText);
            }
        });
        return arrowsgroup;
    },

    /**
     * draws the backsplash or waterfall side item
     * @param canvas
     * @param shape
     * @param corners
     * @param drawingEdge
     * @param depth
     * @param selectedColor
     * @param onEdgeEdit
     * @param label
     * @param activeDrawingShape
     */
    drawRectangleSideItem: (
        canvas: fabric.Canvas,
        shape: DrawingShape,
        corners: CornerInfo[],
        drawingEdge: DrawingEdge,
        depth: number,
        selectedColor: ColorModel,
        onEdgeEdit: (editedArrow: ArrowInfo, shape: DrawingShape) => void,
        label: string,
        activeDrawingShape: DrawingShape
    ) => {
        if (!depth || !corners || !shape) {
            return;
        }

        drawingTool.removeObjectByName(canvas, `${CANVAS_OBJECT_TYPE_PREFIX.BACKSPLASH_GROUP}_${drawingEdge.id}`, true);

        const optSideItem: IObjectOptions = {
            fill: 'transparent',
            selectable: false,
            stroke: variables.borderColor,
            strokeWidth: GREY_STROKE_WIDTH_BACKSPLASH,
            strokeUniform: true,
            name: CANVAS_OBJECT_TYPE_PREFIX.POLYGON_BACKSPLASH + uniqueId(),
        };

        optSideItem.data = {
            parentName: shape?.canvasObjectName,
        };

        // restore applied Corners
        const cornerRadiiInfo = Object.values({ ...shape.cornerMap });
        // EE-968 ensure corners are sorted by filtering each individually (helps with side item arrow display)
        corners = [
            ...cornerRadiiInfo.filter((cornerRadiusInfo) => cornerRadiusInfo.id === drawingEdge.startCornerId),
            ...cornerRadiiInfo.filter((cornerRadiusInfo) => cornerRadiusInfo.id === drawingEdge.endCornerId),
        ];

        // create side item from removed drawing edge corner, offset and depth of the side item

        const dirVec = corners[1].coords.subtract(corners[0].coords, true);
        const normalVec = dirVec.rotate(Math.PI / 2, undefined, true).normalize();
        const lbPt = corners[0].coords.add(normalVec.multiply(OFFSET_BACKSPLASH, true), true);
        const rbPt = corners[1].coords.add(normalVec.multiply(OFFSET_BACKSPLASH, true), true);
        const ltPt = lbPt.add(normalVec.multiply(depth, true), true);
        const rtPt = rbPt.add(normalVec.multiply(depth, true), true);

        const sideItemOutlinePoints = [ltPt, rtPt, rbPt, lbPt]; // EE-968 no need to convert order, already sorted

        // at this point, the coords are relative to the group object, so this must be subtracted for absolute coords
        const ltPtCopy = new Vec2(ltPt.x, ltPt.y);
        const rtPtCopy = new Vec2(rtPt.x, rtPt.y);
        const rbPtCopy = new Vec2(rbPt.x, rbPt.y);
        const lbPtCopy = new Vec2(lbPt.x, lbPt.y);
        const groupCenterPoint = shape.fabricGroup.getCenterPoint();
        const groupCenterVec2 = new Vec2(groupCenterPoint.x, groupCenterPoint.y);
        const sideItemOutlinePointsAbsolute = [
            ltPtCopy.subtract(groupCenterVec2),
            rtPtCopy.subtract(groupCenterVec2),
            rbPtCopy.subtract(groupCenterVec2),
            lbPtCopy.subtract(groupCenterVec2),
        ];

        const sideItemPolygon = new fabric.Polygon(sideItemOutlinePoints, optSideItem);

        // add arrows object to side item
        const sideItemArrows = drawingTool.addRectangleSideItemArrows(
            canvas,
            shape,
            sideItemOutlinePointsAbsolute,
            drawingEdge,
            onEdgeEdit,
            activeDrawingShape
        );

        // add side item label
        const midPoint = MathFunctions.getMidpoint(ltPt, rbPt); // only one midpoint needed (EE-966) //
        const sideItemLabel = new fabric.IText(label, {
            fontFamily: variables.fontFamily,
            stroke: selectedColor?.overlayColor ?? BLACK,
            fill: selectedColor?.overlayColor ?? BLACK,
            left: midPoint.x,
            top: midPoint.y,
            height: 50,
            strokeWidth: LABEL_STROKE_WIDTH,
            textAlign: 'center',
            editable: false,
            fontSize: 3,
            hoverCursor: 'pointer',
            hasControls: false,
            lockMovementX: true,
            lockMovementY: true,
            name: `${CANVAS_OBJECT_TYPE_PREFIX.BACKSPLASH_LABEL}`,
            data: {
                parentName: shape?.canvasObjectName,
                highlightable: true,
                inInterior: true,
            },
        });

        drawingTool.rotateAndPositionSideItemLabel(sideItemLabel, dirVec);

        if (sideItemArrows) {
            // add side item group
            const sideItemGroup = new fabric.Group([sideItemPolygon, sideItemLabel], {
                selectable: false,
            });

            sideItemGroup.name = `${CANVAS_OBJECT_TYPE_PREFIX.BACKSPLASH_GROUP}_${drawingEdge.id}`;
            shape.fabricGroup.addWithUpdate(sideItemGroup);
        }

        canvas.renderAll();
    },

    getBumpOutOutline: (drawingEdge: DrawingEdge): Vec2[] => {
        const bumpOutOutline: Vec2[] = [];
        bumpOutOutline.push(drawingEdge.startDisplayVec);
        bumpOutOutline.push(
            bumpOutOutline[0].add(
                drawingEdge.dirVec.normalize(true).multiply(drawingEdge.bumpOut!.clockwiseRemainderLength, true),
                true
            )
        );
        bumpOutOutline.push(
            bumpOutOutline[1].add(drawingEdge.normalVec.multiply(drawingEdge.bumpOut!.depth, true), true)
        );
        bumpOutOutline.push(
            bumpOutOutline[2].add(drawingEdge.dirVec.normalize(true).multiply(drawingEdge.bumpOut!.length, true), true)
        );
        bumpOutOutline.push(
            bumpOutOutline[3].subtract(drawingEdge.normalVec.multiply(drawingEdge.bumpOut!.depth, true), true)
        );
        bumpOutOutline.push(drawingEdge.endDisplayVec);
        return bumpOutOutline;
    },

    addBumpOutArrows: (
        canvas: fabric.Canvas,
        shape: DrawingShape,
        drawingEdge: DrawingEdge,
        onEdit: (editedArrow: ArrowInfo, shape: DrawingShape) => void,
        activeDrawingShape: DrawingShape
    ): fabric.Group | undefined => {
        if (!shape) {
            return;
        }
        let bumpOutArrowsgroup: fabric.Group | undefined = new fabric.Group([], { selectable: false });
        const arrowOpt: IObjectOptions = {
            stroke: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
            strokeWidth: MEASUREMENT_STROKE_WIDTH,
            fill: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
            selectable: false,
            data: {},
        };

        const groupCenterPoint = shape.fabricGroup.getCenterPoint();
        const groupCenterVec2 = new Vec2(groupCenterPoint.x, groupCenterPoint.y);
        const bumpOutOutline = drawingTool.getBumpOutOutline(drawingEdge);
        const bumpOutOutlineAbsolute = bumpOutOutline.map((corner) => corner.subtract(groupCenterVec2, true));

        // create and return bumpout arrow group
        const arrows = getBumpOutArrowsFromOutlines(bumpOutOutlineAbsolute);

        // determine constraints for bumpOut arrows
        const displayableEdgeLength = MathFunctions.getDistanceBetween(
            drawingEdge.startDisplayVec,
            drawingEdge.endDisplayVec
        );
        const remainderSideMax = displayableEdgeLength - drawingEdge.bumpOut!.length - EDGE_LENGTH_INPUT_LIMIT.MIN;
        const middleOfBumpOut = drawingEdge!.startDisplayVec
            .add(
                drawingEdge!.dirVec.normalize(true).multiply(drawingEdge!.bumpOut!.clockwiseRemainderLength, true),
                true
            )
            .add(drawingEdge!.dirVec.normalize(true).multiply(drawingEdge!.bumpOut!.length / 2, true), true);
        const distanceFromBumpOutMiddleToStart = MathFunctions.getDistanceBetween(
            middleOfBumpOut,
            drawingEdge.startDisplayVec
        );
        const distanceFromBumpOutMiddleToEnd = MathFunctions.getDistanceBetween(
            middleOfBumpOut,
            drawingEdge.endDisplayVec
        );
        const bumpOutLengthMax =
            (Math.min(distanceFromBumpOutMiddleToStart, distanceFromBumpOutMiddleToEnd) - EDGE_LENGTH_INPUT_LIMIT.MIN) *
            2;

        if (drawingEdge.bumpOut) {
            if (drawingEdge.bumpOut.arrowsInfo.length > 0) {
                drawingEdge.bumpOut.arrowsInfo = [];
            }
            drawingEdge.bumpOut.arrowsInfo = arrows.map((arrow, index) => {
                return {
                    path: arrow.path,
                    label: arrow.label,
                    isVisibleInCanvas: true,
                    drawingEdgeId: drawingEdge.id + '-' + index.toString(),
                    isInterior: true,
                    isDiagonalCorner: false,
                    isActiveForEdgeCalc: false,
                    maximumLength: index === BUMPOUT_LENGTH ? bumpOutLengthMax : remainderSideMax,
                    minimumLength: EDGE_LENGTH_INPUT_LIMIT.MIN,
                } as ArrowInfo;
            });
        }

        drawingEdge?.bumpOut?.arrowsInfo?.map((arrowInfo, index) => {
            drawingTool.removeObjectByName(canvas, `${CANVAS_OBJECT_TYPE_PREFIX.BUMPOUT_ARROW}_${index}`, true);

            bumpOutArrowsgroup = drawingTool.createArrowGroup(arrowInfo, arrowOpt);
            if (!bumpOutArrowsgroup) {
                return;
            }

            // add labels for each arrow
            const arrowPts = arrowInfo.path;

            const arrowPtsAbsolute = [arrowPts[0].add(groupCenterVec2, true), arrowPts[1].add(groupCenterVec2, true)];
            const lineSegmentAbsolute = {
                startPoint: arrowPtsAbsolute[0],
                endPoint: arrowPtsAbsolute[1],
            };
            bumpOutArrowsgroup.data = {
                parentName: shape.canvasObjectName,
                highlightable: true,
                name: `${CANVAS_OBJECT_TYPE_PREFIX.BUMPOUT_ARROW}_${index}`,
            };

            arrowInfo.label = '';
            arrowInfo.size = roundToArrowLengthGranularity(
                MathFunctions.getDistanceBetween(arrowPtsAbsolute[0], arrowPtsAbsolute[1])
            );

            shape.editableLabels.push(
                new EditableLengthLabel(
                    shape,
                    lineSegmentAbsolute,
                    arrowInfo,
                    '',
                    onEdit,
                    canvas,
                    BLACK,
                    activeDrawingShape
                ) //arrowInfo.label!
            );
            shape.fabricGroup.add(bumpOutArrowsgroup);
        });
        return bumpOutArrowsgroup;
    },

    bringAllLabelsForward: (canvas: fabric.Canvas): void => {
        const targetList = canvas.getObjects();
        targetList.map((target) => {
            if (
                target.name &&
                (target.name.includes(CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MEASURE_LABEL) ||
                    target.name.includes(CANVAS_OBJECT_TYPE_PREFIX.SHAPE_LABEL) ||
                    target.name.includes(CANVAS_OBJECT_TYPE_PREFIX.EDGE_NAME))
            ) {
                canvas.bringToFront(target);
            }
        });
    },

    selectShape: (
        {
            allShapes,
            canvas,
            activeDrawingShape,
            color,
        }: {
            allShapes: DrawingShape[];
            canvas: fabric.Canvas;
            activeDrawingShape: DrawingShape | undefined;
            color: string;
        } // eslint-disable-next-line sonarjs/cognitive-complexity
    ): void => {
        drawingTool.removeObjectByName(canvas, CANVAS_OBJECT_TYPE_PREFIX.ACTIVE_SHAPE_HIGHLIGHTS, true);

        // all non-selected shapes get light labels and no letters
        allShapes.forEach((shape, index) => {
            if (index !== FAUX_SHAPE_INDEX) {
                if (shape.id === activeDrawingShape?.id) {
                    shape.darkenAllLabels();
                } else {
                    shape.lightenAllLabels?.();
                    shape.removeLabelLetters?.();
                }
            }
        });

        const targetList = canvas.getObjects();

        // eslint-disable-next-line sonarjs/cognitive-complexity
        targetList.map((target: any) => {
            if (target.name?.includes(CANVAS_OBJECT_TYPE_SUFFIX.SHAPE_GROUP)) {
                const objectsInGroup = target.getObjects().filter((obj: any) => obj.data && obj.data.highlightable);
                objectsInGroup.forEach((item: any) => {
                    const isInterior = !!item.data?.inInterior;
                    const isTheSelectedShape = item.data.parentName === activeDrawingShape?.canvasObjectName;
                    const labelColor = isTheSelectedShape
                        ? DRAWING_SHAPE_COLOR_PALETTE.ACTIVE.STROKE
                        : DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE;

                    if (item.getObjects) {
                        const children = item.getObjects();
                        children?.map((child: fabric.Object) => {
                            child.set({
                                stroke: isInterior ? color : labelColor,
                                fill: isInterior ? color : labelColor,
                            });
                        });
                    } else if (isInterior) {
                        item.set({ stroke: color, fill: color });
                    } else {
                        item.set({ stroke: labelColor });

                        // fill the shape name
                        if (!item.name?.toLocaleLowerCase().includes(CANVAS_OBJECT_TYPE_PREFIX.SHAPE_LABEL)) {
                            item.set({ fill: labelColor });
                        }

                        if (item.name) {
                            if (item.name.includes(CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MEASURE_LABEL)) {
                                item.text = isTheSelectedShape ? item.data.rawText : `${item.data.value}${INCH_QUOTE}`;
                            }
                            if (item.name.includes(CANVAS_OBJECT_TYPE_PREFIX.BACKSPLASH_MEASURE_LABEL)) {
                                item.text = isTheSelectedShape
                                    ? item.data.rawText
                                    : `   ${item.data.value}${INCH_QUOTE}`;
                            }

                            // this is probably only required if it's the selectedShape since we set the stroke above ^^^ already
                            // change edge stroke color
                            // change corner radii arc stroke color  // doesn't currently work without parentName which causes arc to fill
                            // change corner diagonal stroke color
                            if (
                                item.name.includes(CANVAS_OBJECT_TYPE_PREFIX.SHAPE_EDGE) ||
                                item.name.includes(CANVAS_OBJECT_TYPE_PREFIX.CORNER_RADIUS_ARC) ||
                                item.name.includes(CANVAS_OBJECT_TYPE_PREFIX.DIAGONAL)
                            ) {
                                // TODO - set the color of the edges!!!!
                                // const getSideItems = (
                                //     shapeName: string,
                                //     edgeName: string
                                // ): SideItemInfoModel | undefined => {
                                //     const allSideItemsForShape = shapesSideItems[shapeName ?? ''];
                                //     if (!allSideItemsForShape) {
                                //         return undefined;
                                //     }
                                //     return Object.values(allSideItemsForShape).reduce(
                                //         (found: SideItemInfoModel | undefined, sideItems) => {
                                //             return (
                                //                 found ??
                                //                 sideItems.find(
                                //                     (item) =>
                                //                         `drawing_edge_${item.arrowInfo.drawingEdgeId}` === edgeName
                                //                 )
                                //             );
                                //         },
                                //         undefined
                                //     );
                                // };
                                //
                                // const matched = getSideItems(item.data.name, item.lineCoords?.name);
                                // console.log('==edge colors', item, shapesSideItems, matched);
                                const selectedEdgeColor = variables.secondaryColor;
                                item.set({
                                    stroke: isTheSelectedShape ? selectedEdgeColor : labelColor,
                                });
                            }
                        }
                    }
                });
            }
        });

        // Fix for EE-1111 / uses difference technique for radius corners, bump outs and radius outs as adding data to those objects causes filler issues
        allShapes.forEach((shape) => {
            shape.fabricGroup.forEachObject((element: fabric.Object) => {
                if (
                    element.name?.includes(CANVAS_OBJECT_TYPE_PREFIX.CORNER_RADIUS_ARC) ||
                    element.name?.includes(CANVAS_OBJECT_TYPE_PREFIX.BUMP_OUT) ||
                    element.name?.includes(CANVAS_OBJECT_TYPE_PREFIX.RADIUS_OUT)
                ) {
                    if (shape.canvasObjectName === activeDrawingShape?.canvasObjectName) {
                        element.set({ stroke: variables.secondaryColor });
                    } else {
                        element.set({ stroke: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE });
                    }
                }
            });
        });

        canvas.renderAll();
    },

    getShapeLabels: (canvas: fabric.Canvas): Array<fabric.Object> => {
        return canvas
            .getObjects()
            .filter((obj) => obj.name?.toLocaleLowerCase().includes(CANVAS_OBJECT_TYPE_PREFIX.SHAPE_LABEL));
    },

    // eslint-disable-next-line sonarjs/cognitive-complexity
    addEdgeName: (
        canvas: fabric.Canvas,
        defaultEdgeName: string,
        color: string,
        drawingShapes: DrawingShape[]
    ): void => {
        const shapeLabels = drawingTool.getShapeLabels(canvas);

        if (shapeLabels?.length) {
            drawingTool.removeObjectByName(canvas, CANVAS_OBJECT_TYPE_PREFIX.EDGE_NAME);

            shapeLabels.map((label) => {
                const targetList = canvas.getObjects();
                targetList.map((target: any) => {
                    if (target.name?.includes(label.data?.parentName + CANVAS_OBJECT_TYPE_SUFFIX.SHAPE_GROUP)) {
                        drawingShapes.forEach((drawingShape: DrawingShape) => {
                            if (drawingShape.canvasObjectName === label.data?.parentName) {
                                const parsedShapeName = label.name?.replace(
                                    `${CANVAS_OBJECT_TYPE_PREFIX.SHAPE_LABEL}`,
                                    ''
                                );

                                const shapeEdgeName = drawingShape?.edge?.consumerDesc ?? drawingShape?.edge?.name;
                                const edgeLabel = shapeEdgeName ?? defaultEdgeName;

                                const edgeText = new fabric.Text(edgeLabel, {
                                    stroke: color,
                                    fill: color,
                                    strokeWidth: MEASUREMENT_STROKE_WIDTH,
                                    fontSize: 3.5,
                                    fontFamily: 'Helvetica',
                                    selectable: false,
                                    name: `${CANVAS_OBJECT_TYPE_PREFIX.EDGE_NAME}${parsedShapeName}`,
                                    data: {
                                        parentName: label.data?.parentName,
                                        highlightable: true,
                                        inInterior: true,
                                    },
                                });

                                if (label.aCoords && label.height) {
                                    // To avoid tslint error
                                    edgeText.top = label.aCoords.tl.y + label.height;
                                    edgeText.left = label.aCoords.tl.x;
                                }

                                canvas.add(edgeText);
                                drawingTool.replaceITextWithEllipsisIfNeeded(
                                    edgeText,
                                    getOutlinePointsFromShape(drawingShape)
                                );
                            }
                        });
                    }
                });
            });
        }
    },

    addCornerRadiiMeasurements: (
        canvas: fabric.Canvas,
        shapes: DrawingShape[],
        color: string,
        onEdit: (editedCorner: CornerInfo, drawingShape: DrawingShape) => void,
        cornerRadii: CornerRadiusModel[]
    ): void => {
        // populate cornerRadiusInfo array with initial values

        shapes.map((shape: DrawingShape) => {
            Object.values(shape?.cornerMap).map((corner, index: number) => {
                corner.size = corner.size ?? DEFAULT_CORNER_RADIUS;

                if (corner.productId === undefined) {
                    corner.label = formatCornerRadiusLabel(corner.size);
                }
                if (!corner.isVisibleInCanvas) {
                    return;
                }
                if (corner.diagonalLength) {
                    return;
                } // temporary fix for EE-1314
                shape.editableLabels.push(
                    new EditableCornerLabel(
                        shape,
                        corner,
                        CORNER_RADIUS_SIGN,
                        onEdit,
                        canvas,
                        color,
                        index,
                        cornerRadii
                    )
                );
            });
        });
    },

    createCircleOnCorner: (
        radiusInfo: CornerInfo,
        parentDrawingShape: DrawingShape,
        selectedOption: CornerRadiusModel | DiagonalCornerModel,
        identifierName: string
    ): fabric.Circle => {
        const augmentedName =
            identifierName +
            '_' +
            (isCornerRadiusModel(selectedOption)
                ? SELECTION_CIRCLES.FOR_ROUNDED_RADII
                : SELECTION_CIRCLES.FOR_DIAGONAL_CORNERS);
        return new fabric.Circle({
            left: radiusInfo.coords.x,
            top: radiusInfo.coords.y,
            radius: SELECTION_CIRCLE_RADIUS,
            fill: variables.secondaryColor,
            opacity: SELECTION_CIRCLE_OPACITY,
            originX: 'center',
            originY: 'center',
            hasControls: false,
            hasBorders: false,
            selectable: false,
            name: augmentedName,
            data: {
                parentDrawingShape,
                cornerId: radiusInfo.id,
                selectedOption,
            },
        });
    },

    createCircleOnEdge: (
        edgeInfo: DrawingEdge,
        parentDrawingShape: DrawingShape,
        selectedOption: CustomSideModel
    ): fabric.Circle => {
        let edgeMidPoint = MathFunctions.getMidpoint(edgeInfo.startDisplayVec, edgeInfo.endDisplayVec);
        let depth = 0;
        if (containsRadiusOut(edgeInfo)) {
            depth = edgeInfo!.radiusSagitta!;
        } else if (containsBumpOut(edgeInfo)) {
            edgeMidPoint = edgeInfo.startDisplayVec
                .add(edgeInfo.dirVec.normalize(true).multiply(edgeInfo.bumpOut!.clockwiseRemainderLength, true), true)
                .add(edgeInfo.dirVec.normalize(true).multiply(edgeInfo.bumpOut!.length / 2, true), true);
            depth = edgeInfo!.bumpOut!.depth!;
        }

        if (depth) {
            edgeMidPoint.add(edgeInfo.normalVec.multiply(depth, true), false);
        }

        return new fabric.Circle({
            left: edgeMidPoint.x,
            top: edgeMidPoint.y,
            radius: SELECTION_CIRCLE_RADIUS,
            fill: variables.secondaryColor,
            opacity: SELECTION_CIRCLE_OPACITY,
            originX: 'center',
            originY: 'center',
            hasControls: false,
            hasBorders: false,
            selectable: false,
            name: CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE + '_' + SELECTION_CIRCLES.FOR_CUSTOM_SIDES,
            data: {
                parentDrawingShape,
                edgeId: edgeInfo.id,
                selectedOption,
            },
        });
    },

    canReplaceDiagonalWithCornerRadius: (
        selectedOptionOnCard: CornerRadiusModel | DiagonalCornerModel,
        radiusInfo: CornerInfo
    ): boolean => {
        return !!(isCornerRadiusModel(selectedOptionOnCard) && radiusInfo.diagonalLength);
    },

    radiusIsChanging: (radiusInfo: CornerInfo, radius: number): boolean => {
        return radiusInfo.size !== radius;
    },

    canAddSelectionCircleForDiagonal: (radiusInfo: CornerInfo, diagonalCornerType: DiagonalCornerType): boolean => {
        return !!(
            (diagonalCornerType.includeInsideCorners && radiusInfo.angle! < 0 && !radiusInfo.diagonalLength) ||
            (diagonalCornerType.includeOutsideCorners && radiusInfo.angle! > 0 && !radiusInfo.diagonalLength) ||
            (diagonalCornerType.removeDiagonalCorner && radiusInfo.diagonalLength)
        );
    },

    addCirclesToOutlinePointsOfShape: (
        canvas: fabric.Canvas,
        parentDrawingShape: DrawingShape,
        selectedOptionOnCard: CornerRadiusModel | DiagonalCornerModel,
        diagonalCornerType: DiagonalCornerType
    ): void => {
        if (!parentDrawingShape) {
            return;
        }

        // Calculating the radius of that will be attempted to be applied
        const [minRadius]: RadiusRangeValues = constrainMinMaxToDefaultMinimum(
            getMinMaxFromCorner(selectedOptionOnCard)
        );
        const newRadius = isCornerRadiusModel(selectedOptionOnCard)
            ? getDefaultRadiusFromRadii(selectedOptionOnCard)
            : 0;

        Object.values(parentDrawingShape.cornerMap)
            .filter((radiusInfo) => {
                const canRadius =
                    isCornerRadiusModel(selectedOptionOnCard) &&
                    canApplyRadius(radiusInfo, parentDrawingShape, minRadius);
                const isVisible = !!radiusInfo.isVisibleInCanvas;
                const canDiagonal =
                    !isCornerRadiusModel(selectedOptionOnCard) &&
                    drawingTool.canAddSelectionCircleForDiagonal(radiusInfo, diagonalCornerType);
                const canReplace =
                    drawingTool.radiusIsChanging(radiusInfo, newRadius) ||
                    drawingTool.canReplaceDiagonalWithCornerRadius(selectedOptionOnCard, radiusInfo);
                return (canRadius || canDiagonal) && isVisible && canReplace; // true if we can apply the changes and should show the circle
            })
            .map((radiusInfo) => {
                const circle = drawingTool.createCircleOnCorner(
                    radiusInfo,
                    parentDrawingShape,
                    selectedOptionOnCard,
                    CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE
                );
                canvas.add(circle);
            });
    },

    addCirclesToEdgesOfShape: (
        canvas: fabric.Canvas,
        parentDrawingShape: DrawingShape,
        selectedOptionOnCard: CustomSideModel
    ): void => {
        if (!parentDrawingShape) {
            return;
        }

        Object.values(parentDrawingShape.drawingEdges)
            .filter(
                (edgeInfo) =>
                    edgeInfo.isVisibleInCanvas &&
                    edgeInfo.customSideType !== selectedOptionOnCard.type &&
                    !(
                        edgeInfo.customSideType === undefined &&
                        selectedOptionOnCard.type === CustomSideType.REMOVE_CUSTOM_SIDE
                    ) &&
                    Math.ceil(MathFunctions.getDistanceBetween(edgeInfo.startDisplayVec, edgeInfo.endDisplayVec)) >=
                        MINIMUM_CUSTOM_SIDE_LENGTH
            )
            .map((edgeInfo) => {
                const circle = drawingTool.createCircleOnEdge(edgeInfo, parentDrawingShape, selectedOptionOnCard);
                canvas.add(circle);
            });
    },

    removeObjectByName: (canvas: fabric.Canvas, name: string, strictMode = false): void => {
        const targetList = strictMode
            ? canvas.getObjects().filter((obj) => obj.name === name)
            : canvas.getObjects().filter((obj) => obj.name?.toLocaleLowerCase().includes(name));
        targetList.map((obj) => canvas.remove(obj));

        // also remove from any fabric groups
        if (name !== CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE) {
            const groupTargetList = canvas.getObjects();
            groupTargetList.map((target: any) => {
                if (target.name?.includes(CANVAS_OBJECT_TYPE_SUFFIX.SHAPE_GROUP)) {
                    const objectsInGroup = strictMode
                        ? target.getObjects().filter((obj: fabric.Object) => obj.name === name)
                        : target
                              .getObjects()
                              .filter((obj: fabric.Object) => obj.name?.toLocaleLowerCase().includes(name));
                    objectsInGroup.map((obj: fabric.Object) => {
                        target.remove(obj);
                    });
                }
            });
        }
    },
};
export default drawingTool;
